// src/pages/dashboard/SessionPrep.js
// Route: /dashboard/prep/:clientId
// AI Session Prep Assistant — chat interface for practitioners to prepare
// for upcoming client sessions using all available client data.
// Query param ?session=<prepSessionId> loads an existing session in read-only mode.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, Sparkles } from 'lucide-react';
import { auth } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { startPrep, sendMessage, getPrepSession } from '../../services/session-prep-service';

// ---------------------------------------------------------------------------
// Helper: relative time label
// ---------------------------------------------------------------------------
function relativeTime(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 10) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// DataRichnessIndicator (module scope — no re-mount on every render)
// ---------------------------------------------------------------------------
function DataRichnessIndicator({ dataRichness }) {
  if (!dataRichness) return null;
  const { level, label, details } = dataRichness;

  const detailParts = [];
  if (details) {
    if (details.sessions > 0) detailParts.push(`${details.sessions} session${details.sessions !== 1 ? 's' : ''}`);
    if (details.notes > 0) detailParts.push(`${details.notes} note${details.notes !== 1 ? 's' : ''}`);
    if (details.intakes > 0) detailParts.push(`${details.intakes} intake form${details.intakes !== 1 ? 's' : ''}`);
    if (details.transcripts > 0) detailParts.push(`${details.transcripts} transcript${details.transcripts !== 1 ? 's' : ''}`);
    if (details.summaries > 0) detailParts.push(`${details.summaries} AI summar${details.summaries !== 1 ? 'ies' : 'y'}`);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: i <= level
                  ? 'linear-gradient(135deg, #7c3aed, #22d3ee)'
                  : '#E5E7EB',
                flexShrink: 0,
              }}
            />
          ))}
        </div>
        <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>{label}</span>
      </div>
      {detailParts.length > 0 && (
        <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0, paddingLeft: '1px' }}>
          {detailParts.join(' · ')}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TypingIndicator (module scope)
// ---------------------------------------------------------------------------
function TypingIndicator() {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
      <div style={{ maxWidth: '72%' }}>
        <div
          style={{
            background: '#F3EAFF',
            borderRadius: '18px',
            padding: '12px 16px',
            display: 'flex',
            gap: '5px',
            alignItems: 'center',
          }}
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: '#9C5FE0',
                display: 'inline-block',
                animation: `typingBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChatBubble (module scope)
// ---------------------------------------------------------------------------
function ChatBubble({ message }) {
  const isUser = message.role === 'user';
  const time = relativeTime(message.timestamp);

  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <div style={{ maxWidth: '78%' }}>
        {!isUser && (
          <p style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}>
            <span>✨</span>
            <span>AI Session Prep</span>
          </p>
        )}
        <div
          style={{
            borderRadius: '18px',
            padding: '10px 16px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            ...(isUser
              ? { background: '#1F2937', color: '#fff' }
              : { background: '#F3EAFF', color: '#374151' }
            ),
          }}
        >
          <p style={{ fontSize: '14px', lineHeight: '1.55', whiteSpace: 'pre-wrap', margin: 0 }}>
            {message.content}
          </p>
        </div>
        {time && (
          <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '3px', textAlign: isUser ? 'right' : 'left' }}>
            {time}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suggested question chip (module scope)
// ---------------------------------------------------------------------------
const STARTER_QUESTIONS = [
  'What should I focus on today?',
  'Summarize our recent sessions',
  'Any patterns or themes to be aware of?',
];

function StarterChips({ onSelect, disabled }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px', justifyContent: 'flex-start' }}>
      {STARTER_QUESTIONS.map((q) => (
        <button
          key={q}
          type="button"
          onClick={() => onSelect(q)}
          disabled={disabled}
          style={{
            padding: '6px 14px',
            borderRadius: '20px',
            border: '1px solid #DDD6FE',
            background: '#FAF5FF',
            color: '#6D28D9',
            fontSize: '13px',
            fontWeight: 500,
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s ease, border-color 0.15s ease',
            opacity: disabled ? 0.5 : 1,
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.currentTarget.style.background = '#EDE9FE';
              e.currentTarget.style.borderColor = '#7C3AED';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#FAF5FF';
            e.currentTarget.style.borderColor = '#DDD6FE';
          }}
        >
          {q}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SessionPrep (main component)
// ---------------------------------------------------------------------------
const SessionPrep = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const viewSessionId = searchParams.get('session'); // read-only mode

  // Auth
  const [practitionerId, setPractitionerId] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  // Session state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientName, setClientName] = useState('');
  const [dataRichness, setDataRichness] = useState(null);
  const [prepSessionId, setPrepSessionId] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [hasWelcome, setHasWelcome] = useState(false);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [exchangeCount, setExchangeCount] = useState(0);
  const [isLastExchange, setIsLastExchange] = useState(false);
  const [sendError, setSendError] = useState('');

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Auth
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!auth) { setAuthLoaded(true); return; }
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setAuthLoaded(true); } }, 3000);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!done) {
        done = true;
        clearTimeout(timeout);
        if (user) setPractitionerId(user.uid);
        setAuthLoaded(true);
      }
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  // ---------------------------------------------------------------------------
  // Load or start session (once auth is ready)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!authLoaded || !practitionerId) return;

    if (viewSessionId) {
      // Read-only mode: load existing session
      loadExistingSession(practitionerId, viewSessionId);
    } else {
      // New session mode: call startSessionPrep
      initNewSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoaded, practitionerId, viewSessionId, clientId]);

  const loadExistingSession = useCallback(async (uid, sessionId) => {
    setLoading(true);
    setIsReadOnly(true);
    try {
      const session = await getPrepSession(uid, sessionId);
      if (!session) {
        setError('Prep session not found.');
        setLoading(false);
        return;
      }
      setClientName(session.clientName || '');
      setDataRichness(session.dataRichness || null);
      setPrepSessionId(session.id);
      setExchangeCount(session.exchangeCount || 0);

      // Convert stored messages
      const stored = (session.messages || []).map((m, idx) => ({
        id: `stored-${idx}`,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
      }));
      setMessages(stored);
    } catch (err) {
      setError('Failed to load prep session.');
    }
    setLoading(false);
  }, []);

  const initNewSession = useCallback(async () => {
    setLoading(true);
    try {
      const result = await startPrep(clientId);
      setPrepSessionId(result.prepSessionId);
      setClientName(result.clientName || '');
      setDataRichness(result.dataRichness || null);

      // Build welcome message from data richness
      const dr = result.dataRichness;
      const details = dr?.details || {};
      const parts = [];
      if (details.notes > 0) parts.push(`${details.notes} session note${details.notes !== 1 ? 's' : ''}`);
      if (details.intakes > 0) parts.push(`${details.intakes} intake response${details.intakes !== 1 ? 's' : ''}`);
      if (details.summaries > 0) parts.push(`${details.summaries} AI summar${details.summaries !== 1 ? 'ies' : 'y'}`);
      if (details.transcripts > 0) parts.push(`${details.transcripts} transcript${details.transcripts !== 1 ? 's' : ''}`);

      let welcomeText;
      if (parts.length === 0) {
        welcomeText = `I've reviewed ${result.clientName || 'your client'}'s record. I have their booking history available, but limited session documentation so far. What would you like to know before your session?`;
      } else {
        welcomeText = `I've reviewed ${result.clientName || 'your client'}'s history. I have access to ${parts.join(', ')}. What would you like to know before your session?`;
      }

      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: welcomeText,
        timestamp: new Date(),
      }]);
      setHasWelcome(true);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Premier')) {
        setError('AI Session Prep is available on the Premier plan.');
      } else {
        setError(msg || 'Failed to start prep session. Please try again.');
      }
    }
    setLoading(false);
  }, [clientId]);

  // ---------------------------------------------------------------------------
  // Auto-scroll
  // ---------------------------------------------------------------------------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // ---------------------------------------------------------------------------
  // Send message
  // ---------------------------------------------------------------------------
  const handleSend = useCallback(async (text) => {
    const msg = (text || inputText).trim();
    if (!msg || sending || isLastExchange || !prepSessionId) return;

    setInputText('');
    setSendError('');
    setSending(true);

    // Optimistically add user message
    const userMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: msg,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const result = await sendMessage(prepSessionId, msg);
      const aiMsg = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMsg]);
      setExchangeCount(result.exchangeCount);
      setIsLastExchange(result.isLastExchange);
    } catch (err) {
      setSendError(err.message || 'Failed to get response. Please try again.');
    }
    setSending(false);
    inputRef.current?.focus();
  }, [inputText, sending, isLastExchange, prepSessionId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStartNew = () => {
    navigate(`/dashboard/prep/${clientId}`);
    window.location.reload();
  };

  // ---------------------------------------------------------------------------
  // Render: loading
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-3xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-64" />
            <div className="h-8 bg-gray-200 rounded w-48" />
            <div className="h-4 bg-gray-100 rounded w-80" />
            <div className="bg-white rounded-xl border border-gray-200" style={{ height: '500px' }} />
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">
            {viewSessionId ? 'Loading prep session…' : 'Gathering client data…'}
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: error
  // ---------------------------------------------------------------------------
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-3xl mx-auto px-4">
          <button
            onClick={() => navigate(`/dashboard/clients/${clientId}`)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Client Profile
          </button>
          <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
            <p className="font-semibold text-gray-900 mb-2">Unable to start prep session</p>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate(`/dashboard/clients/${clientId}`)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
            >
              Back to Client Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: chat
  // ---------------------------------------------------------------------------
  const exchangeLimit = 10;
  const atLimit = isLastExchange || exchangeCount >= exchangeLimit;
  const inputDisabled = sending || atLimit || isReadOnly;

  return (
    <div className="min-h-screen bg-gray-50" style={{ display: 'flex', flexDirection: 'column' }}>

      {/* ── Page header ── */}
      <div className="max-w-3xl mx-auto w-full px-4 pt-6 pb-4">

        {/* Breadcrumb */}
        <nav style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', marginBottom: '16px' }}>
          <span
            role="button" tabIndex={0}
            onClick={() => navigate('/dashboard/clients')}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/clients'); }}
            style={{ color: '#7c3aed', fontWeight: 500, cursor: 'pointer' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#6D28D9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#7c3aed'; }}
          >
            Clients
          </span>
          <span style={{ color: '#D1D5DB' }}>/</span>
          <span
            role="button" tabIndex={0}
            onClick={() => navigate(`/dashboard/clients/${clientId}`)}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/dashboard/clients/${clientId}`); }}
            style={{ color: '#7c3aed', fontWeight: 500, cursor: 'pointer' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#6D28D9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#7c3aed'; }}
          >
            {clientName || 'Client'}
          </span>
          <span style={{ color: '#D1D5DB' }}>/</span>
          <span style={{ color: '#374151', fontWeight: 500 }}>
            {isReadOnly ? 'Prep Session' : 'Session Prep'}
          </span>
        </nav>

        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', margin: 0 }}>
                {clientName || 'Client'}
              </h1>
              <span style={{
                fontSize: '12px', fontWeight: 600, color: '#7C3AED',
                background: '#F5F3FF', borderRadius: '12px', padding: '2px 10px',
                display: 'flex', alignItems: 'center', gap: '4px',
              }}>
                <Sparkles style={{ width: '11px', height: '11px' }} />
                AI Session Prep
                {isReadOnly && <span style={{ fontWeight: 400, color: '#9CA3AF' }}> · Read Only</span>}
              </span>
            </div>
            {dataRichness && <DataRichnessIndicator dataRichness={dataRichness} />}
          </div>
          <button
            onClick={() => navigate(`/dashboard/clients/${clientId}`)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '13px', color: '#6B7280', cursor: 'pointer',
              background: 'none', border: 'none', padding: 0,
              transition: 'color 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#374151'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7280'; }}
          >
            <ArrowLeft style={{ width: '14px', height: '14px' }} />
            Back to Client Profile
          </button>
        </div>
      </div>

      {/* ── Chat container ── */}
      <div style={{ flex: 1, maxWidth: '768px', width: '100%', margin: '0 auto', padding: '0 16px 24px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div
          style={{
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 320px)',
            minHeight: '420px',
            overflow: 'hidden',
          }}
        >

          {/* ── Messages area ── */}
          <div
            style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 8px', display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {messages.map((msg) => (
              <ChatBubble key={msg.id} message={msg} />
            ))}

            {/* Starter chips below welcome message */}
            {hasWelcome && messages.length === 1 && !sending && !isReadOnly && (
              <div style={{ paddingLeft: '4px' }}>
                <StarterChips onSelect={handleSend} disabled={sending} />
              </div>
            )}

            {/* Typing indicator */}
            {sending && <TypingIndicator />}

            <div ref={messagesEndRef} />
          </div>

          {/* ── Send error ── */}
          {sendError && (
            <div style={{ margin: '0 16px 8px', padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ fontSize: '13px', color: '#DC2626', margin: 0 }}>{sendError}</p>
              <button onClick={() => setSendError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F87171', padding: '2px', lineHeight: 1 }}>
                ✕
              </button>
            </div>
          )}

          {/* ── Input area ── */}
          <div style={{ borderTop: '1px solid #F3F4F6', padding: '14px 16px' }}>
            {atLimit && !isReadOnly ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '10px' }}>
                  You've reached the limit for this prep session.
                </p>
                <button
                  onClick={handleStartNew}
                  style={{
                    padding: '8px 20px',
                    background: '#7C3AED',
                    color: '#fff',
                    borderRadius: '10px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#6D28D9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#7C3AED'; }}
                >
                  <Sparkles style={{ width: '14px', height: '14px' }} />
                  Start New Prep Session
                </button>
              </div>
            ) : isReadOnly ? (
              <p style={{ fontSize: '13px', color: '#9CA3AF', textAlign: 'center', margin: 0 }}>
                This is a past prep session — read only.
              </p>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <textarea
                    ref={inputRef}
                    value={inputText}
                    onChange={(e) => { setInputText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                    onKeyDown={handleKeyDown}
                    disabled={inputDisabled}
                    placeholder="Ask about your client…"
                    rows={1}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid #E5E7EB',
                      fontSize: '14px',
                      color: '#111827',
                      resize: 'none',
                      lineHeight: '1.45',
                      outline: 'none',
                      fontFamily: 'inherit',
                      overflow: 'hidden',
                      minHeight: '42px',
                      maxHeight: '120px',
                      transition: 'border-color 0.15s ease',
                      background: inputDisabled ? '#F9FAFB' : '#fff',
                    }}
                    onFocus={(e) => { if (!inputDisabled) e.target.style.borderColor = '#7C3AED'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; }}
                  />
                  <button
                    onClick={() => handleSend()}
                    disabled={!inputText.trim() || inputDisabled}
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '12px',
                      background: (!inputText.trim() || inputDisabled) ? '#E5E7EB' : '#7C3AED',
                      border: 'none',
                      cursor: (!inputText.trim() || inputDisabled) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (inputText.trim() && !inputDisabled) e.currentTarget.style.background = '#6D28D9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = (!inputText.trim() || inputDisabled) ? '#E5E7EB' : '#7C3AED';
                    }}
                  >
                    <Send style={{ width: '16px', height: '16px', color: (!inputText.trim() || inputDisabled) ? '#9CA3AF' : '#fff' }} />
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                  <p style={{ fontSize: '11px', color: '#D1D5DB', margin: 0 }}>
                    Press Enter to send · Shift+Enter for new line
                  </p>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>
                    {exchangeCount} of {exchangeLimit} exchanges used
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default SessionPrep;
